import { useCallback, useEffect, useState } from "react";
import { api } from "../../../lib/api";
import {
  NormalizedRowsPage,
  normalizeRowsPage,
  RowStatus,
} from "../lib/rowContract";
import {
  NormalizedDuplicate,
  normalizeDuplicates,
  groupDuplicatesByRow,
} from "../lib/duplicateContract";

export type ReadinessSnapshot = {
  invalid: number;
  duplicates_unresolved: number;
  manual_review: number;
  unresolved_total: number;
  ready_for_commit: boolean;
  job_status: string;
  job_version: number;
};

const READINESS_ZERO: ReadinessSnapshot = {
  invalid: 0,
  duplicates_unresolved: 0,
  manual_review: 0,
  unresolved_total: 0,
  ready_for_commit: false,
  job_status: "",
  job_version: 0,
};

/**
 * Paginated row fetch for Step 4. Filters + page are inputs; the server is
 * the source of `total`. Consumers must never sum row lengths locally to
 * derive counts.
 */
export function useImportRows(
  jobId: string,
  params: { status?: RowStatus | null; page: number; limit: number },
): {
  data: NormalizedRowsPage | null;
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
} {
  const [data, setData] = useState<NormalizedRowsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams();
      if (params.status) q.set("status", params.status);
      q.set("page", String(params.page));
      q.set("limit", String(params.limit));
      const { data } = await api.get(`/import/jobs/${jobId}/rows?${q.toString()}`);
      setData(normalizeRowsPage(data));
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jobId, params.status, params.page, params.limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}

export function useImportDuplicates(jobId: string): {
  duplicates: NormalizedDuplicate[];
  byRow: Record<string, NormalizedDuplicate[]>;
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
} {
  const [duplicates, setDuplicates] = useState<NormalizedDuplicate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/import/jobs/${jobId}/duplicates`);
      setDuplicates(normalizeDuplicates(data));
    } catch (e) {
      setError(e);
      setDuplicates([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    duplicates,
    byRow: groupDuplicatesByRow(duplicates),
    loading,
    error,
    refetch: load,
  };
}

export function useImportReadiness(jobId: string): {
  readiness: ReadinessSnapshot;
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
} {
  const [readiness, setReadiness] = useState<ReadinessSnapshot>(READINESS_ZERO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<ReadinessSnapshot>(`/import/jobs/${jobId}/readiness`);
      setReadiness({ ...READINESS_ZERO, ...data });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  return { readiness, loading, error, refetch: load };
}
